import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, useWindowDimensions } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { useAuthStore } from '../store/useAuthStore';
import { useActivityStore } from '../store/useActivityStore';
import { useStrokeStore } from '../store/useStrokeStore';
import { StrokeSyncService } from '../services/StrokeSyncService';
import { TaskQueueService } from '../services/TaskQueueService';
import { ChildFriendlyBackground } from '../components/ChildFriendlyBackground';
import { FriendlyButton } from '../components/FriendlyButton';
import { User, ArrowRight } from 'lucide-react-native';

type Props = StackScreenProps<RootStackParamList, 'NameEntry'>;

export function NameEntryScreen({ navigation, route }: Props) {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;
    const { classId, serverUrl, initialName } = route.params;
    const [name, setName] = useState(initialName || '');
    const [joinError, setJoinError] = useState<string | null>(null);
    const [isJoining, setIsJoining] = useState(false);

    const handleJoin = async () => {
        const cleanName = name.trim();
        if (!cleanName || isJoining) return;
        setJoinError(null);
        setIsJoining(true);
        useActivityStore.getState().resetActivities();
        useStrokeStore.getState().clearStrokes();

        try {
            const result = await TaskQueueService.resolveOrCreateStudent(cleanName, classId, serverUrl);
            if (!result) throw new Error('Unable to resolve or create student');

            const tasks = await TaskQueueService.fetchPendingTasks(result.studentId, serverUrl);
            useAuthStore.getState().joinClass(classId, cleanName, result.studentId, serverUrl, 'KS1');
            useActivityStore.getState().setStudentId(result.studentId);
            useActivityStore.getState().loadPreFetchedTasks(tasks);
            StrokeSyncService.startSync();
            navigation.replace('TaskQueue');
        } catch (error) {
            console.error('[NameEntryScreen] Unable to join class:', error);
            setJoinError("Let's try again! Check your connection and tap Let's Go.");
        } finally {
            setIsJoining(false);
        }
    };

    return (
        <ChildFriendlyBackground variant="light">
            <View style={[styles.content, isLandscape && styles.landscapeContent]}>
                <View style={styles.iconContainer}>
                    <User size={48} color="#1890FF" strokeWidth={2.5} />
                </View>

                <Text style={[styles.title, isLandscape && styles.landscapeTitle]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>What's Your Name?</Text>
                <Text style={[styles.subtitle, isLandscape && styles.landscapeSubtitle]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                    Class: {classId}
                </Text>

                <TextInput
                    style={[styles.input, isLandscape && styles.landscapeInput]}
                    placeholder="Type your name here"
                    placeholderTextColor="#94A3B8"
                    value={name}
                    onChangeText={setName}
                    autoCapitalize="words"
                    returnKeyType="go"
                    onSubmitEditing={handleJoin}
                    accessibilityLabel="Name input"
                    accessibilityHint="Type your first name here"
                />

                <FriendlyButton
                    variant="primary"
                    size="large"
                    icon={ArrowRight}
                    onPress={handleJoin}
                    disabled={isJoining}
                    accessibilityLabel="Let's Go"
                    accessibilityHint="Join the class with the name you typed"
                >
                    {isJoining ? 'One moment...' : "Let's Go!"}
                </FriendlyButton>

                {joinError ? <Text style={styles.joinError} accessibilityLiveRegion="polite" allowFontScaling={true} maxFontSizeMultiplier={1.5}>{joinError}</Text> : null}

                <TouchableOpacity
                    style={[styles.backButton, isLandscape && styles.landscapeBackButton]}
                    onPress={() => navigation.goBack()}
                    accessibilityLabel="Back"
                    accessibilityRole="button"
                    accessibilityHint="Go back to the previous screen"
                >
                    <Text style={styles.backButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>← Back</Text>
                </TouchableOpacity>
            </View>
        </ChildFriendlyBackground>
    );
}

const styles = StyleSheet.create({
    content: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    landscapeContent: {
        paddingHorizontal: 48,
    },
    iconContainer: {
        width: 96,
        height: 96,
        borderRadius: 48,
        backgroundColor: '#E6F7FF',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 24,
        shadowColor: '#1890FF',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 3,
    },
    title: {
        fontSize: 28,
        fontWeight: '800',
        color: '#1E293B',
        marginBottom: 8,
        textAlign: 'center',
    },
    landscapeTitle: {
        fontSize: 24,
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 16,
        color: '#64748B',
        marginBottom: 32,
        textAlign: 'center',
    },
    landscapeSubtitle: {
        marginBottom: 16,
    },
    input: {
        width: '100%',
        maxWidth: 400,
        height: 64,
        borderWidth: 2,
        borderColor: '#BFDBFE',
        borderRadius: 16,
        paddingHorizontal: 20,
        fontSize: 20,
        backgroundColor: '#FFFFFF',
        color: '#1E293B',
        marginBottom: 24,
        textAlign: 'center',
    },
    landscapeInput: {
        height: 52,
        fontSize: 18,
        marginBottom: 16,
        maxWidth: 500,
    },
    joinError: { color: '#92400E', fontSize: 16, fontWeight: '600', textAlign: 'center', marginTop: 16, paddingHorizontal: 12 },
    backButton: {
        marginTop: 20,
        paddingVertical: 12,
        paddingHorizontal: 24,
        minHeight: 56,
    },
    landscapeBackButton: {
        marginTop: 12,
    },
    backButtonText: {
        color: '#64748B',
        fontSize: 16,
        fontWeight: '600',
    },
});
